import React, { useState, useMemo } from 'react';
import { RawTrader, TraderDerivation, ReviewItem } from './types';
import { initials, money, starColors, chipStyle, getTraderReviewPool } from './constants';

interface ReviewsTabProps {
  trader: RawTrader;
  derivation: TraderDerivation;
}

export const ReviewsTab: React.FC<ReviewsTabProps> = ({ trader }) => {
  const [rvStar, setRvStar] = useState<string>('all');
  const [rvSort, setRvSort] = useState<string>('new');
  const [liked, setLiked] = useState<Record<string, boolean>>({});
  const [newStars, setNewStars] = useState<number>(0);
  const [newText, setNewText] = useState<string>('');
  const [myReviews, setMyReviews] = useState<Record<string, { s: number; text: string; date: string }>>({});
  const [replyOpen, setReplyOpen] = useState<string>('');
  const [replyDraft, setReplyDraft] = useState<Record<string, string>>({});
  const [myReplies, setMyReplies] = useState<Record<string, Array<{ text: string; date: string }>>>({});

  // Review Pool Data
  const { pool, maxMonths } = useMemo(() => getTraderReviewPool(trader), [trader]);

  const REF = useMemo(() => new Date(2026, 8, 9), []);

  const rvAll: ReviewItem[] = useMemo(() => {
    const list: ReviewItem[] = pool.map((r, i) => {
      const months = Math.max(1, Math.min(r.months, maxMonths - Math.floor((i * maxMonths) / pool.length)));
      const daysAgo = Math.round((i / Math.max(pool.length - 1, 1)) * Math.min(trader.days - 30, 150)) + 7;
      const fmt = (off: number) => {
        const dt = new Date(REF.getTime() - off * 86400000);
        return (
          String(dt.getDate()).padStart(2, '0') +
          '/' +
          String(dt.getMonth() + 1).padStart(2, '0') +
          '/' +
          dt.getFullYear()
        );
      };
      return {
        ...r,
        id: r.name,
        months,
        date: fmt(daysAgo),
        replyDate: r.reply ? fmt(Math.max(daysAgo - 1, 1)) : '',
      };
    });

    const mine = myReviews[trader.name];
    const myMonths = Math.max(1, Math.min(4, maxMonths));
    if (mine) {
      list.unshift({
        id: '__me',
        name: 'Phạm Thanh Tuyền (bạn)',
        s: mine.s,
        date: mine.date,
        months: myMonths,
        cap: money(Math.min(trader.cap, 50000)),
        helpful: 0,
        text: mine.text,
        mine: true,
      });
    }

    return list;
  }, [pool, maxMonths, trader.days, trader.cap, trader.name, myReviews, REF]);

  const alreadyReviewed = !!myReviews[trader.name];
  const canReview = trader.days >= 30 && !alreadyReviewed;
  const myMonths = Math.max(1, Math.min(4, maxMonths));

  const rvAvg = useMemo(() => {
    return rvAll.reduce((a, r) => a + r.s, 0) / (rvAll.length || 1);
  }, [rvAll]);

  const filteredReviews = useMemo(() => {
    let list = rvAll.slice();
    if (rvStar === 'reply') {
      list = list.filter((r) => r.reply);
    } else if (rvStar !== 'all') {
      list = list.filter((r) => r.s === +rvStar);
    }

    if (rvSort === 'high') list.sort((a, b) => b.s - a.s);
    if (rvSort === 'low') list.sort((a, b) => a.s - b.s);
    return list;
  }, [rvAll, rvStar, rvSort]);

  const starLabels = ['Chọn số sao', 'Rất không hài lòng', 'Chưa hài lòng', 'Bình thường', 'Hài lòng', 'Rất hài lòng'];

  const handleSubmitReview = () => {
    if (!canReview || !newStars || !newText.trim()) return;
    setMyReviews((prev) => ({
      ...prev,
      [trader.name]: { s: newStars, text: newText.trim(), date: '09/09/2026' },
    }));
    setNewStars(0);
    setNewText('');
  };

  const handleSendReply = (reviewId: string) => {
    const draft = replyDraft[reviewId] || '';
    if (!draft.trim()) return;
    setMyReplies((prev) => ({
      ...prev,
      [reviewId]: [...(prev[reviewId] || []), { text: draft.trim(), date: '09/09/2026' }],
    }));
    setReplyDraft((prev) => ({ ...prev, [reviewId]: '' }));
    setReplyOpen('');
  };

  const AV_COLORS = [
    ['#E7F5F5', '#0B7A7A'],
    ['#FDF3E3', '#A06C10'],
    ['#EFF2F7', '#3F4A5A'],
    ['#EFF8F3', '#157A4C'],
  ];

  return (
    <div className="flex flex-wrap lg:flex-nowrap gap-5 items-start">
      {/* Left Sidebar: Rating Statistics */}
      <div className="w-full lg:w-[320px] flex-shrink-0 card-box p-5 lg:sticky lg:top-[88px]">
        <div className="text-[15px] font-bold text-[#16202E] mb-4">Đánh giá từ nhà đầu tư</div>

        <div className="flex items-baseline gap-3 mb-2">
          <div className="text-4xl font-bold text-[#16202E] leading-none">{rvAvg.toFixed(1)}</div>
          <div>
            <div className="flex gap-0.5">
              {starColors(Math.round(rvAvg)).map((color, idx) => (
                <span key={idx} style={{ color }} className="text-sm">
                  ★
                </span>
              ))}
            </div>
            <div className="text-xs text-[#8B95A3] mt-1">{rvAll.length} đánh giá</div>
          </div>
        </div>

        {/* Breakdown bars */}
        <div className="flex flex-col gap-2 mt-4">
          {[5, 4, 3, 2, 1].map((star) => {
            const count = rvAll.filter((r) => r.s === star).length;
            const pct = rvAll.length ? (count / rvAll.length) * 100 : 0;
            const barColor = star >= 4 ? '#1FA463' : star === 3 ? '#E39A2B' : '#E0524D';
            const isActive = rvStar === String(star);

            return (
              <div
                key={star}
                onClick={() => setRvStar((prev) => (prev === String(star) ? 'all' : String(star)))}
                className="cursor-pointer flex items-center gap-2.5 hover:opacity-75 transition-opacity"
              >
                <div
                  style={{ color: isActive ? '#0E9C9C' : '#5C6779', fontWeight: isActive ? 600 : 400 }}
                  className="font-mono text-xs w-6 flex-shrink-0"
                >
                  {star}★
                </div>
                <div className="flex-1 h-2 bg-[#F0F3F7] !rounded-full overflow-hidden">
                  <div
                    style={{ width: `${pct}%`, background: barColor }}
                    className="h-full !rounded-full transition-all duration-300"
                  ></div>
                </div>
                <div className="font-mono text-xs text-[#8B95A3] w-5 text-right flex-shrink-0">{count}</div>
              </div>
            );
          })}
        </div>

        {/* Aspects evaluation */}
        <div className="mt-5 pt-4 border-t border-[#EEF1F5] flex flex-col gap-3">
          {[
            { k: 'Kỷ luật rủi ro', v: (rvAvg + 0.2 > 5 ? 5 : rvAvg + 0.2).toFixed(1) },
            { k: 'Tính minh bạch', v: rvAvg.toFixed(1) },
            { k: 'Độ ổn định kết quả', v: (rvAvg - 0.1).toFixed(1) },
            { k: 'Giao tiếp', v: (rvAvg - 0.4).toFixed(1) },
          ].map((aspect) => (
            <div key={aspect.k}>
              <div className="flex justify-between items-baseline mb-1">
                <span className="text-xs text-[#5C6779]">{aspect.k}</span>
                <span className="font-mono text-xs font-semibold text-[#16202E]">{aspect.v}</span>
              </div>
              <div className="h-1.5 bg-[#F0F3F7] !rounded-full overflow-hidden">
                <div
                  style={{
                    width: `${Math.min((parseFloat(aspect.v) / 5) * 100, 100)}%`,
                    background: '#0E9C9C',
                  }}
                  className="h-full !rounded-full"
                ></div>
              </div>
            </div>
          ))}
        </div>

        {/* Criteria note */}
        <div className="mt-5 p-3 bg-[#F7F9FB] !rounded-lg text-xs text-[#5C6779] leading-relaxed">
          Chỉ nhà đầu tư đã theo vốn trader này ít nhất 30 ngày mới được gửi đánh giá. Mỗi tài khoản gửi một đánh giá.
        </div>
      </div>

      {/* Right Column: Review List & Write Review */}
      <div className="flex-1 min-w-0 flex flex-col gap-4 w-full">
        {/* Filters and sorting bar */}
        <div className="card-box p-3.5 flex flex-wrap gap-2.5 items-center">
          <div className="flex gap-1.5 flex-wrap">
            {[
              ['all', 'Tất cả'],
              ['5', '5 sao'],
              ['4', '4 sao'],
              ['3', '3 sao'],
              ['2', '2 sao'],
              ['reply', 'Có phản hồi'],
            ].map(([k, label]) => {
              const active = rvStar === k;
              return (
                <button
                  key={k}
                  onClick={() => setRvStar(k)}
                  style={chipStyle(active)}
                  className="cursor-pointer py-2 px-3 text-xs font-semibold !rounded-lg border transition-colors"
                >
                  {label}
                </button>
              );
            })}
          </div>

          <select
            value={rvSort}
            onChange={(e) => setRvSort(e.target.value)}
            className="ml-auto text-xs font-semibold text-[#16202E] py-2 px-2.5 border border-[#E7EBF0] !rounded-lg bg-white outline-none cursor-pointer"
          >
            <option value="new">Mới nhất</option>
            <option value="high">Điểm cao nhất</option>
            <option value="low">Điểm thấp nhất</option>
          </select>
        </div>

        {/* Write Review Form Card */}
        <div className="card-box p-5">
          <div className="flex gap-3.5 items-start">
            <div className="w-10 h-10 !rounded-full bg-[#E39A2B] text-[#06223B] flex items-center justify-center text-xs font-bold flex-shrink-0">
              PT
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex flex-wrap items-center gap-2.5">
                <span className="text-sm font-bold text-[#16202E]">Viết đánh giá của bạn</span>
                <span
                  style={{
                    backgroundColor: alreadyReviewed ? '#EFF2F7' : canReview ? '#EFF8F3' : '#FDF6EA',
                    color: alreadyReviewed ? '#5C6779' : canReview ? '#157A4C' : '#A06C10',
                  }}
                  className="text-[10px] font-bold py-1 px-2 !rounded"
                >
                  {alreadyReviewed ? 'ĐÃ GỬI ĐÁNH GIÁ' : canReview ? 'ĐỦ ĐIỀU KIỆN' : 'CHƯA ĐỦ ĐIỀU KIỆN'}
                </span>
              </div>

              {/* Star rating selector */}
              <div className="flex items-center gap-3 mt-3">
                <span className="text-xs text-[#5C6779]">Chấm điểm</span>
                <div className="flex gap-1">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setNewStars(star)}
                      style={{ color: star <= newStars ? '#E39A2B' : '#DDE3EA' }}
                      className="cursor-pointer text-xl leading-none bg-transparent border-0 p-0 hover:opacity-80 transition-opacity"
                    >
                      ★
                    </button>
                  ))}
                </div>
                <span className="text-xs text-[#8B95A3]">{starLabels[newStars]}</span>
              </div>

              <textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Bạn theo vốn trader này bao lâu, điều gì tốt và điều gì cần cải thiện?"
                className="w-full mt-3 min-h-[82px] border border-[#E7EBF0] !rounded-lg p-3 text-sm text-[#16202E] outline-none bg-[#FAFBFD] focus:border-[#0E9C9C] transition-colors resize-y"
              />

              <div className="flex flex-wrap items-center gap-3 mt-3">
                <button
                  type="button"
                  onClick={handleSubmitReview}
                  disabled={!canReview || newStars === 0 || !newText.trim()}
                  style={{
                    background: canReview && newStars > 0 && newText.trim() ? '#E39A2B' : '#F0F3F7',
                    color: canReview && newStars > 0 && newText.trim() ? '#06223B' : '#A6AFBC',
                  }}
                  className="cursor-pointer py-2.5 px-4 !rounded-lg font-bold text-xs disabled:cursor-not-allowed transition-colors"
                >
                  Gửi đánh giá
                </button>
                <div className="text-xs text-[#A6AFBC] flex-1 min-w-[200px]">
                  {alreadyReviewed
                    ? 'Bạn đã đánh giá trader này. Có thể chỉnh sửa trong 7 ngày kể từ lúc gửi.'
                    : canReview
                      ? `Bạn đã theo vốn trader này ${myMonths} tháng nên đủ điều kiện đánh giá.`
                      : 'Cần theo vốn tối thiểu 30 ngày mới gửi được đánh giá.'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Empty State */}
        {filteredReviews.length === 0 && (
          <div className="card-box border-dashed border-[#D8DEE6] p-12 text-center">
            <div className="text-base font-bold text-[#16202E] mb-1.5">Chưa có đánh giá nào ở mức này</div>
            <div className="text-sm text-[#6B7684]">Chọn "Tất cả" để xem toàn bộ đánh giá.</div>
          </div>
        )}

        {/* Reviews List */}
        {filteredReviews.map((r, i) => {
          const isLiked = !!liked[r.id || r.name];
          const openReply = replyOpen === (r.id || r.name);
          const currentDraft = replyDraft[r.id || r.name] || '';
          const thread = [];
          if (r.reply) {
            thread.push({
              ini: initials(trader.name),
              name: trader.name,
              isTrader: true,
              date: r.replyDate,
              text: r.reply,
              avBg: '#06223B',
              avFg: '#35D0CE',
            });
          }
          (myReplies[r.id || r.name] || []).forEach((extra) => {
            thread.push({
              ini: 'PT',
              name: 'Phạm Thanh Tuyền (bạn)',
              isTrader: false,
              date: extra.date,
              text: extra.text,
              avBg: '#FDF3E3',
              avFg: '#A06C10',
            });
          });

          const avColor = r.mine ? ['#FDF3E3', '#A06C10'] : AV_COLORS[i % 4];

          return (
            <div key={r.id || r.name} className="card-box p-5">
              <div className="flex gap-3.5 items-start">
                <div
                  style={{ background: avColor[0], color: avColor[1] }}
                  className="w-10 h-10 !rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                >
                  {initials(r.name)}
                </div>

                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-sm font-bold text-[#16202E]">{r.name}</span>
                    <div className="flex gap-0.5">
                      {starColors(r.s).map((c, starIdx) => (
                        <span key={starIdx} style={{ color: c }} className="text-xs">
                          ★
                        </span>
                      ))}
                    </div>
                    {!r.mine && (
                      <span className="text-[10px] font-bold py-0.5 px-2 !rounded bg-[#EFF8F3] text-[#157A4C]">
                        ĐÃ THEO VỐN
                      </span>
                    )}
                    <span className="font-mono text-xs text-[#A6AFBC] ml-auto">{r.date}</span>
                  </div>

                  <div className="text-xs text-[#8B95A3] mt-1">
                    Theo vốn {r.months} tháng · quy mô {r.cap}
                  </div>

                  <div className="text-sm leading-relaxed text-[#3F4A5A] mt-2.5">{r.text}</div>

                  {/* Thread Replies */}
                  {thread.length > 0 && (
                    <div className="mt-3.5 pl-3.5 border-l-2 border-[#E7EBF0] flex flex-col gap-3">
                      {thread.map((rep, repIdx) => (
                        <div key={repIdx}>
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <div
                              style={{ background: rep.avBg, color: rep.avFg }}
                              className="w-6 h-6 !rounded-full flex items-center justify-center text-[10px] font-bold"
                            >
                              {rep.ini}
                            </div>
                            <span className="text-xs font-bold text-[#16202E]">{rep.name}</span>
                            {rep.isTrader && (
                              <span className="text-[10px] font-bold py-0.5 px-1.5 !rounded bg-[#E7F5F5] text-[#0B7A7A]">
                                TRADER
                              </span>
                            )}
                            <span className="font-mono text-xs text-[#A6AFBC]">{rep.date}</span>
                          </div>
                          <div className="text-xs leading-relaxed text-[#3F4A5A] pl-8">{rep.text}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Action Bar */}
                  <div className="flex items-center gap-4 mt-3">
                    <button
                      type="button"
                      onClick={() =>
                        setLiked((prev) => ({
                          ...prev,
                          [r.id || r.name]: !prev[r.id || r.name],
                        }))
                      }
                      style={{ color: isLiked ? '#0E9C9C' : '#8B95A3' }}
                      className="cursor-pointer flex items-center gap-1.5 text-xs font-semibold bg-transparent border-0 p-0 hover:opacity-75 transition-opacity"
                    >
                      <div
                        style={{ background: isLiked ? '#0E9C9C' : '#8B95A3' }}
                        className="w-1.5 h-1.5 !rounded-full"
                      ></div>
                      Hữu ích ({r.helpful + (isLiked ? 1 : 0)})
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setReplyOpen((prev) => (prev === (r.id || r.name) ? '' : r.id || r.name))
                      }
                      style={{ color: openReply ? '#0E9C9C' : '#8B95A3' }}
                      className="cursor-pointer text-xs font-semibold bg-transparent border-0 p-0 hover:opacity-75 transition-opacity"
                    >
                      {thread.length ? `Trả lời (${thread.length})` : 'Trả lời'}
                    </button>

                    <span className="text-xs text-[#A6AFBC] cursor-pointer hover:underline">Báo cáo</span>
                  </div>

                  {/* Reply Input Box */}
                  {openReply && (
                    <div className="mt-3 flex gap-2.5 items-start">
                      <div className="w-7 h-7 !rounded-full bg-[#E39A2B] text-[#06223B] flex items-center justify-center text-[10.5px] font-bold flex-shrink-0">
                        PT
                      </div>
                      <div className="flex-1 min-w-0">
                        <textarea
                          value={currentDraft}
                          onChange={(e) =>
                            setReplyDraft((prev) => ({
                              ...prev,
                              [r.id || r.name]: e.target.value,
                            }))
                          }
                          placeholder={`Trả lời ${r.name}…`}
                          className="w-full min-h-[64px] border border-[#E7EBF0] !rounded-lg p-2.5 text-xs text-[#16202E] outline-none bg-[#FAFBFD] focus:border-[#0E9C9C] resize-y"
                        />
                        <div className="flex gap-2 mt-2">
                          <button
                            type="button"
                            onClick={() => handleSendReply(r.id || r.name)}
                            disabled={!currentDraft.trim()}
                            style={{
                              background: currentDraft.trim() ? '#E39A2B' : '#F0F3F7',
                              color: currentDraft.trim() ? '#06223B' : '#A6AFBC',
                            }}
                            className="cursor-pointer py-1.5 px-3 !rounded-lg text-xs font-bold transition-colors disabled:cursor-not-allowed"
                          >
                            Gửi trả lời
                          </button>
                          <button
                            type="button"
                            onClick={() => setReplyOpen('')}
                            className="cursor-pointer py-1.5 px-3 !rounded-lg text-xs font-semibold border border-[#E7EBF0] text-[#5C6779] bg-white hover:bg-gray-50 transition-colors"
                          >
                            Huỷ
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {/* Footer Note */}
        <div className="card-box p-4 text-xs text-[#8B95A3] flex justify-between flex-wrap gap-2">
          <div>
            Hiển thị {filteredReviews.length} / {rvAll.length} đánh giá
          </div>
          <div>Nội dung đánh giá do nhà đầu tư gửi, LUMIR không chỉnh sửa</div>
        </div>
      </div>
    </div>
  );
};
